import React, { FC, useState } from 'react'
import moment from 'moment'
import { Box, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { FilterBase } from 'apps/fleet-planner/components/atoms/FilterBase'
import {
  BookableShipmentSavedSearch,
  BookableShipmentSearchQueryParams,
} from 'apps/fleet-planner/types'
import { LAYOUT, Tab, Tabs, Button } from 'transfix-ui'
import FORMATS from 'constants/datetime'
import { DateFilter } from './DateFilter'
import { DateRangeFilter } from './DateRangeFilter'
import { DateRangeFilterMobile } from './DateRangeFilterMobile'
import { getDates } from './PickupDatesFilter'

interface IDropoffDateFilterProps {
  /**
   * @default false
   */
  isSelected: boolean
  value: BookableShipmentSearchQueryParams
  onOpen: () => void
  onClose: () => void
  onChange: (value: BookableShipmentSearchQueryParams) => void
}

export enum DROPOFF_DATE_TABS {
  date = '1 date',
  dateRange = '2+ dates',
}

const getCurrentTab = (filters?: BookableShipmentSavedSearch): DROPOFF_DATE_TABS => {
  const { deliveryEndDate } = filters || {}
  // Order matters here
  if (deliveryEndDate) return DROPOFF_DATE_TABS.dateRange
  return DROPOFF_DATE_TABS.date
}

export const DropoffDatesFilter: FC<IDropoffDateFilterProps> = ({
  isSelected = false,
  onChange,
  onClose,
  onOpen,
  value: valueProp,
}) => {
  const { isMobile } = useBreakpointContext()
  const { filters } = valueProp || {}
  const { deliveryEndDate: deliveryEndDateProp, deliveryStartDate: deliveryStartDateProp } =
    filters || {}
  /** Tabs */
  const [currentTab, setCurrentTab] = useState(getCurrentTab(filters))
  const tabSwitchHandler = (newTab: DROPOFF_DATE_TABS) => {
    setCurrentTab(newTab)
  }

  const dateChangeHandler = (date?: moment.Moment | null) => {
    const deliveryStartDate = moment(date?.toString()).format(FORMATS.yearFirst)
    onChange({
      filters: {
        ...filters,
        deliveryStartDate,
        deliveryEndDate: undefined,
        pickupTimes: undefined,
      },
    })
    onClose()
  }

  const dateRangeChangeHandler = (dates: (Date | undefined)[]) => {
    const [start, end] = dates || []
    const deliveryStartDate = moment(start).format(FORMATS.yearFirst)
    const deliveryEndDate = moment(end).format(FORMATS.yearFirst)
    onChange({
      filters: {
        ...filters,
        deliveryStartDate,
        deliveryEndDate,
        pickupTimes: undefined,
      },
    })
    onClose()
  }

  const clearFiltersHandler = () => {
    onChange({
      filters: {
        ...filters,
        deliveryStartDate: undefined,
        deliveryEndDate: undefined,
        pickupTimes: undefined,
      },
    })
    onClose()
  }

  return (
    <FilterBase
      large
      alignRight
      label='Dropoff Date'
      onOpen={onOpen}
      onClose={onClose}
      isRenderEditMounted={isSelected}
      useClickAway={currentTab !== DROPOFF_DATE_TABS.dateRange}
      renderEdit={
        <Box data-test='render-edit-block'>
          <Box display='flex' alignItems='center'>
            <Typography variant='button'>Add dropoff dates</Typography>
            <Box ml='auto'>
              <Button color='tertiary' onClick={clearFiltersHandler}>
                Clear
              </Button>
            </Box>
          </Box>
          <Tabs value={currentTab} onChange={tabSwitchHandler} layout={LAYOUT.fixed}>
            <Tab label={DROPOFF_DATE_TABS.date}>
              <DateFilter
                value={deliveryStartDateProp}
                onChange={dateChangeHandler}
                onlyFutureDates
              />
            </Tab>
            <Tab label={DROPOFF_DATE_TABS.dateRange}>
              {isMobile ? (
                <DateRangeFilterMobile
                  startDate={deliveryStartDateProp}
                  endDate={deliveryEndDateProp}
                  onChange={dateRangeChangeHandler}
                  onlyFutureDates
                />
              ) : (
                <DateRangeFilter
                  startDate={deliveryStartDateProp}
                  endDate={deliveryEndDateProp}
                  onChange={dateRangeChangeHandler}
                  onlyFutureDates
                />
              )}
            </Tab>
          </Tabs>
        </Box>
      }
      renderDisplay={
        <Box data-test='render-display-block'>
          {deliveryStartDateProp || deliveryEndDateProp ? (
            <Typography variant='button' color='primary'>
              {getDates(deliveryStartDateProp, deliveryEndDateProp)}
            </Typography>
          ) : (
            <Typography variant='button' color='textSecondary'>
              Add dropoff dates
            </Typography>
          )}
        </Box>
      }
      isSelected={isSelected}
    />
  )
}
